import {
    AuthenticationDetails,
    CognitoUserPool,
    CognitoUser,
} from 'amazon-cognito-identity-js';
import { gql, Resolver } from '@apollo/client';
import _get from 'lodash/get';

interface Args {
    clientId: string;
    previousPassword: string;
    proposedPassword: string;
    username: string;
    userPoolId: string;
}

export const changePasswordWithCognito: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
): Promise<CognitoActionResult> => {
    const {
        clientId,
        previousPassword,
        proposedPassword,
        username,
        userPoolId,
    } = args;
    const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: previousPassword,
    });
    const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
    });
    const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
    });

    const authenticateUser = (): Promise<Error | CognitoSessionResult> =>
        new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: result => {
                    const sessionIsValid = result.isValid();
                    // We used to use session.getAccessToken() to use the accessToken for auth,
                    // but now we're using the idToken. Both work from a client perspective; the
                    // backend uses some additional information (userId and email) encoded in the
                    // idToken to implement additional security logic.
                    const tokenInstance = result.getIdToken();
                    const token = tokenInstance.getJwtToken();
                    const expiration = tokenInstance.getExpiration();
                    const issuedAt = tokenInstance.getIssuedAt();

                    resolve({
                        token,
                        expiration,
                        issuedAt,
                        sessionIsValid,
                    });
                },
                onFailure: err => {
                    reject(err);
                },
            });
        });

    const changePassword = (
        oldPassword: string,
        newPassword: string,
    ): Promise<CognitoActionResult> =>
        new Promise((resolve, reject) => {
            cognitoUser.changePassword(
                oldPassword,
                newPassword,
                (error: Error | undefined, result?: string) => {
                    if (error) {
                        reject(error);
                    }

                    if (result === 'SUCCESS') {
                        resolve({
                            success: true,
                        });
                    }

                    reject(
                        new Error(
                            'No result was returned from cognitoUser.changePassword',
                        ),
                    );
                },
            );
        });

    const authResult = await authenticateUser();
    const sessionIsValid = _get(authResult, 'sessionIsValid', false);

    if (sessionIsValid) {
        return changePassword(previousPassword, proposedPassword);
    }

    return {
        success: false,
    };
};

export const CHANGE_PASSWORD_WITH_COGNITO = gql`
    mutation ChangePasswordWithCognito(
        $clientId: String
        $previousPassword: String
        $proposedPassword: String
        $username: String
        $userPoolId: String
    ) {
        changePasswordWithCognito(
            clientId: $clientId
            previousPassword: $previousPassword
            proposedPassword: $proposedPassword
            username: $username
            userPoolId: $userPoolId
        ) @client
    }
`;
